<template>
  <div
    ref="chat"
    v-if="streamerData || testMessages"
    class="fill-height chat-wrap pr-2 pb-2 pt-2"
    :class="{'pl-2': padding}"
  >
    <div>
      <div
        class="msg-wrap mt-2"
        v-for="(message) in computedMessages" :key="message.id"
      >
        <div
          class="reply"
          v-if="message.type === 'reply'"
          :style="{ fontSize: `${replyFontSize}px` }"
        >
          <v-icon
            icon="mdi-reply"
            :size="fontSize"
            class="mr-1 pb-1"
          />
          <span>Replying to @{{ message.metadata.original_sender.username }}: </span>
          <span v-html="messageContentWithEmotes(message.metadata.original_message.content, fontSize * 1.3)"></span>
        </div>
        <span v-if="showBadges">
          <span class="mr-1" v-for="(badge, i) in message.sender.identity.badges" :key="i">
            <img :src="getBadge(badge)" :width="fontSize" :height="fontSize" />
          </span>
        </span>
        <span class="sender" :style="{ color: message.sender.identity.color }">{{ message.sender.username }}: </span>
        <span v-html="messageContentWithEmotes(message.content, fontSize * 1.3)"></span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { Kick } from '@/services/Kick';
import { Utils } from '@/modules/utils';

const props = defineProps({
  streamer: {
    type: String,
    required: false,
  },
  maxMessages: {
    type: Number,
    default: 100,
  },
  showBadges: {
    type: Boolean,
    default: true,
  },
  loadOldMessages: {
    type: Boolean,
    default: true,
  },
  messageColor: {
    type: String,
    default: '#FFFFFF',
  },
  botMessages: {
    type: Boolean,
    default: true,
  },
  fontSize: {
    type: Number,
    default: 14,
  },
  sevenTV: {
    type: Boolean,
    default: true,
  },
  padding: {
    type: Boolean,
    default: true,
  },
  testMessages: Array,
});

const replyFontSize = computed(() => props.fontSize * 0.8);
const fontSizeTxt = computed(() => `${props.fontSize}px`);

let pusher;
let channel;

const sevenTVEmotes = [];

const bots = ['BotRix', 'LogiBot', 'Kicklet', 'KickBot'];

const chat = ref(null);
const messages = ref([]);
const streamerData = ref(null);

const computedMessages = computed(() => {
  if (props.testMessages) {
    return props.testMessages;
  }
  return messages.value;
});

function messageContentWithEmotes (content, emoteSize) {
  if (typeof content !== 'string') {
    return JSON.stringify(content);
  }

  // Replace kick emotes
  const customEmoteRegex = /\[emote:(\d+:(\w*)+)]/g;
  content = content.replace(customEmoteRegex, (match, emoteCode) => {
    const emoteParts = emoteCode.split(':');
    const emoteId = emoteParts[0];
    const emoteName = emoteParts[1];

    const emoteImageUrl = Kick.getEmoteUrl(emoteId);

    return `<img class="mr-1" src="${emoteImageUrl}" alt="${emoteName}" width="${emoteSize}" height="${emoteSize}" />`;
  });

  // Replace 7tv emotes
  if (props.sevenTV) {
    sevenTVEmotes.forEach((emote) => {
      const sevenTVEmoteName = emote.name;
      const sevenTVEmoteUrl = emote.url;

      const sevenTVRegex = new RegExp(
          `(?<!<img[^>]*="[^"]*)\\b${sevenTVEmoteName}\\b(?!")`,
          'g',
      );

      // Replace the 7tv emote name with the image URL in the message
      content = content.replace(
        sevenTVRegex,
          `<img src="${sevenTVEmoteUrl}" alt="${sevenTVEmoteName}" width="${emoteSize}" height="${emoteSize}" />`,
      );
    });
  }

  return content;
}

// eslint-disable-next-line vue/no-setup-props-destructure
if (!props.testMessages) {
  loadData();
}

function loadData () {
  // eslint-disable-next-line vue/no-setup-props-destructure
  Kick.getStreamerData(props.streamer)
    .then(data => {
      streamerData.value = data;
      const id = data.id;
      const chatId = data.chatroom.id;
      fetch7TVEmotes(data.user_id);

      pusher = Kick.getPusher();

      if (props.loadOldMessages) {
        Kick.getChannelMessages(id)
          .then(msg => {
            // workaround for kicks metadata bug
            let arr = msg.data.messages.map(m => {
              let metadata = m.metadata;
              if (typeof metadata === 'string') {
                metadata = JSON.parse(metadata);
              }
              return {
                ...m,
                metadata,
              };
            });

            if (!props.botMessages) {
              arr = arr.filter(m => !bots.includes(m.sender.username));
            }

            messages.value.push(...arr.reverse());
            if (messages.value.length > props.maxMessages) {
              messages.value.splice(0, messages.value.length - props.maxMessages);
            }

            setupSubscription(chatId);
          });
      } else {
        setupSubscription(chatId);
      }
    });
}

function setupSubscription (chatId) {
  channel = pusher.subscribe(`chatrooms.${chatId}.v2`);
  channel.bind('App\\Events\\ChatMessageEvent', (data) => {
    if (props.botMessages || !bots.includes(data.sender.username)) {
      messages.value.push(data);
    }
    if (messages.value.length > props.maxMessages) {
      messages.value.shift();
    }
  });
  channel.bind('App\\Events\\MessageDeletedEvent', (data) => {
    const messageId = data.message.id;
    messages.value = messages.value.filter(message => message.id !== messageId);
  });
  channel.bind('App\\Events\\ChatroomClearEvent', (data) => {
    messages.value = [];
  });
}

function getBadge (badge) {
  if (badge.type === 'subscriber') {
    const badgeResult = streamerData.value.subscriber_badges.filter(sb => sb.months <= badge.count);
    if (badgeResult.length > 0) {
      const badgeObj = badgeResult.sort((a, b) => b.months - a.months)[0];
      return badgeObj.badge_image.src;
    } else {
      return Utils.getImgUrl('badges/default_subscriber.svg');
    }
  } else if (badge.type === 'moderator') {
    return Utils.getImgUrl('badges/moderator.svg');
  } else if (badge.type === 'verified') {
    return Utils.getImgUrl('badges/verified.svg');
  } else if (badge.type === 'founder') {
    return Utils.getImgUrl('badges/founder.svg');
  } else if (badge.type === 'og') {
    return Utils.getImgUrl('badges/og.svg');
  } else if (badge.type === 'vip') {
    return Utils.getImgUrl('badges/vip.svg');
  } else if (badge.type === 'broadcaster') {
    return Utils.getImgUrl('badges/broadcaster.svg');
  } else if (badge.type === 'staff') {
    return Utils.getImgUrl('badges/staff.svg');
  } else if (badge.type === 'sub_gifter') {
    const count = badge.count;
    if (count >= 200) {
      return Utils.getImgUrl('badges/sub_gift_200.svg');
    } else if (count >= 100) {
      return Utils.getImgUrl('badges/sub_gift_100.svg');
    } else if (count >= 50) {
      return Utils.getImgUrl('badges/sub_gift_50.svg');
    } else if (count >= 25) {
      return Utils.getImgUrl('badges/sub_gift_25.svg');
    } else if (count > 0) {
      return Utils.getImgUrl('badges/sub_gift_1.svg');
    }
  }
}

async function fetch7TVEmotes (userId) {
  if (props.sevenTV) {
    try {
      const response1 = fetch(`https://7tv.io/v3/users/kick/${userId}`);
      const response2 = fetch('https://7tv.io/v3/emote-sets/global');

      const responses = await Promise.all([response1, response2]);
      const data1 = await handleResponse(responses[0]);
      const data2 = await handleResponse(responses[1]);

      const emotesData = [...(data1.emote_set?.emotes || []), ...(data2.emotes || [])];
      emotesData.forEach(emote => {
        const emoteName = emote.name;

        if (emote.data.host && emote.data.host.files) {
          const files = emote.data.host.files;
          const emoteUrl = 'https:' + emote.data.host.url + '/' + files.find(file => file.name === '4x.webp').name;

          sevenTVEmotes.push({ name: emoteName, url: emoteUrl });
        }
      });
    } catch (error) {
      console.log('Error fetching 7TV emotes:', error);
    }
  }
}

async function handleResponse (response) {
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return await response.json();
}

onBeforeUnmount(() => {
  channel?.unsubscribe();
  pusher?.disconnect();
});
</script>

<style scoped>
.chat-wrap {
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  justify-content: flex-end;
  color: v-bind(messageColor)
}

.msg-wrap {
  font-weight: bold;
  font-size: v-bind(fontSizeTxt);
}
.sender {
  font-weight: bold;
}
.reply {
  opacity: 0.7;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}
</style>
